import { PURGE } from 'redux-persist';

let initialState = {
    integrations: [],
    // Dynamic OAuth callback state (keyed by provider: gmail, facebook, etc.)
    oauthCallback: {}, // { gmail: { loading, email, error }, facebook: { loading, email, error }, ... }
    // Dynamic integration status (keyed by toolName: gmail, twilio, whatsapp, etc.)
    integrationStatus: {}, // { gmail: { loading, data, error }, twilio: { loading, data, error }, ... }
    // Test connection state (keyed by integrationId)
    integrationTest: {}, // { integrationId: { loading, success, error }, ... }
    // Dynamic token refresh state (keyed by toolName)
    integrationRefresh: {}, // { gmail: { loading, success, error }, ... }
    // Integration tools
    tools: {
        loading: false,
        data: [],
        error: null,
        // Fallback mappings when no tools loaded from database
        defaultMappings: {
            email: 'Gmail',
            sms: 'Twilio',
            whatsapp: 'WhatsApp'
        }
    },
    // Node tools for workflow builder
    nodeTools: {
        loading: false,
        data: [],
        error: null
    },
    // Available integrations by tool name (for dropdowns)
    availableIntegrations: {
        loading: false,
        data: {}, // Keyed by toolName: { ownIntegrations: [...], publicIntegrations: [...] }
        error: null
    },
    // Sales Accounts
    salesAccounts: {
        loading: false,
        data: [],
        error: null
    },
    // Create/Update Sales Account state
    salesAccountForm: {
        loading: false,
        success: false,
        error: null
    },
    // Email Settings (keyed by integrationId)
    emailSettings: {
        loading: false,
        data: {}, // { integrationId: { warmup: {...}, campaign: {...}, stats: {...} } }
        error: null
    }
}

const Integration = (state = initialState, { type, payload }) => {
    switch (type) {
        case PURGE:
            return initialState;
        case 'SET_INTEGRATIONS':
            return { ...state, integrations: payload }

        // Dynamic OAuth Callback (by provider)
        case 'HANDLE_OAUTH_CALLBACK':
            return {
                ...state,
                oauthCallback: {
                    ...state.oauthCallback,
                    [payload.provider]: {
                        loading: true,
                        email: null,
                        error: null
                    }
                }
            }
        case 'SET_OAUTH_CALLBACK_SUCCESS':
            return {
                ...state,
                oauthCallback: {
                    ...state.oauthCallback,
                    [payload.provider]: {
                        loading: false,
                        email: payload.email,
                        metadata: payload.metadata || null,
                        error: null
                    }
                }
            }
        case 'SET_OAUTH_CALLBACK_ERROR':
            return {
                ...state,
                oauthCallback: {
                    ...state.oauthCallback,
                    [payload.provider]: {
                        loading: false,
                        email: null,
                        error: payload.error
                    }
                }
            }
        // Dynamic Integration Status (by toolName)
        case 'GET_INTEGRATION_STATUS':
            return {
                ...state,
                integrationStatus: {
                    ...state.integrationStatus,
                    [payload.toolName]: {
                        ...(state.integrationStatus[payload.toolName] || {}),
                        loading: true,
                        error: null
                    }
                }
            }
        case 'SET_INTEGRATION_STATUS':
            return {
                ...state,
                integrationStatus: {
                    ...state.integrationStatus,
                    [payload.toolName]: {
                        loading: false,
                        data: payload.status,
                        error: null
                    }
                }
            }
        case 'SET_INTEGRATION_STATUS_ERROR':
            return {
                ...state,
                integrationStatus: {
                    ...state.integrationStatus,
                    [payload.toolName]: {
                        loading: false,
                        data: null,
                        error: payload.error
                    }
                }
            }
        
        // Integration Tools
        case 'GET_INTEGRATION_TOOLS':
            return {
                ...state,
                tools: { ...state.tools, loading: true, error: null }
            }
        case 'SET_INTEGRATION_TOOLS':
            return {
                ...state,
                tools: { ...state.tools, loading: false, data: payload, error: null }
            }
            
        // Node Tools
        case 'GET_NODE_TOOLS':
            return {
                ...state,
                nodeTools: { loading: true, data: state.nodeTools.data, error: null }
            }
        case 'SET_NODE_TOOLS_SUCCESS':
            return {
                ...state,
                nodeTools: { loading: false, data: payload, error: null }
            }
        case 'SET_NODE_TOOLS_ERROR':
            return {
                ...state,
                nodeTools: { loading: false, data: state.nodeTools.data, error: payload }
            }
        case 'GET_NODE_DEFINITION':
            return {
                ...state,
                nodeTools: { ...state.nodeTools, loading: true, error: null }
            }
        
        // Dynamic Integration Refresh (by toolName)
        case 'REFRESH_INTEGRATION_TOKEN':
            return {
                ...state,
                integrationRefresh: {
                    ...state.integrationRefresh,
                    [payload.toolName]: { loading: true, success: false, error: null }
                }
            }
        case 'REFRESH_INTEGRATION_TOKEN_SUCCESS':
            return {
                ...state,
                integrationRefresh: {
                    ...state.integrationRefresh,
                    [payload.toolName]: { loading: false, success: true, error: null }
                }
            }
        case 'REFRESH_INTEGRATION_TOKEN_FAILURE':
            return {
                ...state,
                integrationRefresh: {
                    ...state.integrationRefresh,
                    [payload.toolName]: { loading: false, success: false, error: payload.error }
                }
            }

        // Integration Test (by integrationId)
        case 'TEST_INTEGRATION_CONNECTION':
            return {
                ...state,
                integrationTest: {
                    ...state.integrationTest,
                    [payload]: { loading: true, success: false, error: null }
                }
            }
        case 'TEST_INTEGRATION_CONNECTION_SUCCESS':
            return {
                ...state,
                integrationTest: {
                    ...state.integrationTest,
                    [payload.integrationId]: { loading: false, success: true, error: null }
                }
            }
        case 'TEST_INTEGRATION_CONNECTION_FAILURE':
            return {
                ...state,
                integrationTest: {
                    ...state.integrationTest,
                    [payload.integrationId]: { loading: false, success: false, error: payload.error }
                }
            }
        
        // Available Integrations (for dropdowns)
        case 'GET_AVAILABLE_INTEGRATIONS':
            return {
                ...state,
                availableIntegrations: { ...state.availableIntegrations, loading: true, error: null }
            }
        case 'SET_AVAILABLE_INTEGRATIONS':
            return {
                ...state,
                availableIntegrations: {
                    loading: false,
                    data: {
                        ...state.availableIntegrations.data,
                        [payload.toolName]: {
                            ownIntegrations: payload.ownIntegrations,
                            publicIntegrations: payload.publicIntegrations,
                            total: payload.total
                        }
                    },
                    error: null
                }
            }
        case 'SET_AVAILABLE_INTEGRATIONS_ERROR':
            return {
                ...state,
                availableIntegrations: {
                    ...state.availableIntegrations,
                    loading: false,
                    error: payload
                }
            }
        case 'CLEAR_AVAILABLE_INTEGRATIONS':
            return {
                ...state,
                availableIntegrations: {
                    loading: false,
                    data: {},
                    error: null
                }
            }

        // Create Integration
        case 'CREATE_INTEGRATION':
            return {
                ...state,
                createIntegration: { loading: true, success: false, error: null }
            }
        case 'CREATE_INTEGRATION_SUCCESS':
            return {
                ...state,
                createIntegration: { loading: false, success: true, error: null }
            }
        case 'CREATE_INTEGRATION_FAILURE':
            return {
                ...state,
                createIntegration: { loading: false, success: false, error: payload }
            }
        case 'CREATE_INTEGRATION_RESET':
            return {
                ...state,
                createIntegration: { loading: false, success: false, error: null }
            }

        // ============================================================================
        // SALES ACCOUNT REDUCERS
        // ============================================================================

        // Get Sales Accounts
        case 'GET_SALES_ACCOUNTS':
            return {
                ...state,
                salesAccounts: { ...state.salesAccounts, loading: true, error: null }
            }
        case 'SET_SALES_ACCOUNTS':
            return {
                ...state,
                salesAccounts: { loading: false, data: payload, error: null }
            }
        case 'SET_SALES_ACCOUNTS_ERROR':
            return {
                ...state,
                salesAccounts: { ...state.salesAccounts, loading: false, error: payload }
            }

        // Create Sales Account
        case 'CREATE_SALES_ACCOUNT':
            return {
                ...state,
                salesAccountForm: { loading: true, success: false, error: null }
            }
        case 'CREATE_SALES_ACCOUNT_SUCCESS':
            return {
                ...state,
                salesAccountForm: { loading: false, success: true, error: null },
                salesAccounts: {
                    ...state.salesAccounts,
                    data: [...state.salesAccounts.data, payload]
                }
            }
        case 'CREATE_SALES_ACCOUNT_ERROR':
            return {
                ...state,
                salesAccountForm: { loading: false, success: false, error: payload }
            }
        case 'CREATE_SALES_ACCOUNT_RESET':
            return {
                ...state,
                salesAccountForm: { loading: false, success: false, error: null }
            }

        // Update Sales Account
        case 'UPDATE_SALES_ACCOUNT':
            return {
                ...state,
                salesAccountForm: { loading: true, success: false, error: null }
            }
        case 'UPDATE_SALES_ACCOUNT_SUCCESS':
            return {
                ...state,
                salesAccountForm: { loading: false, success: true, error: null },
                salesAccounts: {
                    ...state.salesAccounts,
                    data: state.salesAccounts.data.map(acc =>
                        acc._id === payload._id ? payload : acc
                    )
                }
            }
        case 'UPDATE_SALES_ACCOUNT_ERROR':
            return {
                ...state,
                salesAccountForm: { loading: false, success: false, error: payload }
            }

        // Delete Sales Account
        case 'DELETE_SALES_ACCOUNT':
            return {
                ...state,
                salesAccountForm: { loading: true, success: false, error: null }
            }
        case 'DELETE_SALES_ACCOUNT_SUCCESS':
            return {
                ...state,
                salesAccountForm: { loading: false, success: true, error: null },
                salesAccounts: {
                    ...state.salesAccounts,
                    data: state.salesAccounts.data.filter(acc => acc._id !== payload)
                }
            }
        case 'DELETE_SALES_ACCOUNT_ERROR':
            return {
                ...state,
                salesAccountForm: { loading: false, success: false, error: payload }
            }

        // Set Primary Sales Account
        case 'SET_PRIMARY_SALES_ACCOUNT':
            return {
                ...state,
                salesAccounts: {
                    ...state.salesAccounts,
                    data: state.salesAccounts.data.map(acc => ({
                        ...acc,
                        isPrimary: acc._id === payload
                    }))
                }
            }

        // Email Settings
        case 'GET_EMAIL_SETTINGS':
            return { ...state, emailSettings: { ...state.emailSettings, loading: true, error: null } }
        case 'SET_EMAIL_SETTINGS':
            return { ...state, emailSettings: { loading: false, data: { ...state.emailSettings.data, [payload.integrationId]: payload.settings }, error: null } }

        default:
            return state;
    }
};

export default Integration;
